import { debouncePromise } from '../../../js/utilities/functions/debounce.js'
import TomSelect from 'tom-select'
import logger from '../module/logger.js'

/**
 * @typedef {Object} AddressLookupElements
 * @property {HTMLSelectElement} countryField
 * @property {HTMLInputElement} zipcodeField
 * @property {HTMLInputElement} houseNumberField
 * @property {HTMLInputElement} houseNumberAdditionField
 * @property {HTMLInputElement} streetField
 * @property {HTMLInputElement} cityField
 * @property {HTMLInputElement} extraField
 * @property {HTMLElement} failureElement
 * @property {HTMLInputElement} autocompleteInput
 * @property {HTMLElement} manualInputToggle
 * @property {HTMLElement} manualInputLink
 */

/**
 * @typedef {Object} NLPostcodeResponse
 * @property {string} street - Street name in accordance with BAG Registry
 * @property {string} streetNen - Street name in NEN-5825 notation
 * @property {number} houseNumber - House number (0 for PO boxes)
 * @property {string|null} houseNumberAddition - Addition to house number or null if not found
 * @property {string} postcode - 6 character Dutch postcode (e.g., "2012ES")
 * @property {string} city - Official city name
 * @property {string} cityShort - Shortened city name
 * @property {string} cityId - Unique city identifier
 * @property {string} municipality - Municipality name
 * @property {string} municipalityShort - Shortened municipality name
 * @property {string} municipalityId - Unique municipality identifier
 * @property {string} province - Province name
 * @property {number|null} rdX - Dutch RD coordinate X
 * @property {number|null} rdY - Dutch RD coordinate Y
 * @property {number|null} latitude - WGS-84 latitude
 * @property {number|null} longitude - WGS-84 longitude
 * @property {string|null} bagNumberDesignationId - BAG number designation ID
 * @property {string|null} bagAddressableObjectId - BAG addressable object ID
 * @property {'building'|'house boat site'|'mobile home site'|'PO box'} addressType
 * @property {Array<'residency'|'assembly'|'detention'|'healthcare'|'industry'|'office'|'lodging'|'education'|'sport'|'shopping'|'other'>} purposes
 * @property {number|null} surfaceArea - Surface area in square meters
 * @property {string[]} houseNumberAdditions - List of all house number additions for this postcode/number
 */

/**
 * @typedef {Object} AutocompleteMatch
 * @property {string} value - Full matched address value
 * @property {string} label - Display label for the match
 * @property {string} description - Additional disambiguation info
 * @property {'None'|'Locality'|'PostalCode'|'Street'|'Address'} precision - Match precision level
 * @property {string} context - Context for subsequent autocomplete calls
 * @property {Array<[number, number]>} highlights - Character ranges to highlight
 */

/**
 * @typedef {Object} AutocompleteResponse
 * @property {string|null} [newContext] - Updated context for further requests, null if no update needed
 * @property {AutocompleteMatch[]} matches - Array of matched addresses
 */

/**
 * @typedef {Object} AddressDetailsAddress
 * @property {string} country
 * @property {string|null} locality
 * @property {string|null} street
 * @property {string|null} postcode
 * @property {string|null} building
 * @property {number|null} buildingNumber
 * @property {string|null} buildingNumberAddition
 * @property {string|null} region
 */

/**
 * @typedef {Object|null} AddressDetailsLocation
 * @property {number} latitude
 * @property {number} longitude
 * @property {'Address'|'Street'|'PostalCode'|'Locality'} precision
 */

/**
 * @typedef {Object} AddressDetailsCountry
 * @property {string} name
 * @property {string} iso3Code
 * @property {string} iso2Code
 */

/**
 * @typedef {Object} AddressDetailsResponse
 * @property {string} language
 * @property {AddressDetailsAddress} address
 * @property {string[]} mailLines
 * @property {AddressDetailsLocation} location - WGS-84 coordinates or null if not available
 * @property {boolean} isPoBox
 * @property {AddressDetailsCountry} country
 * @property {Object|null} [details] - Country-specific details, structure varies by country
 */

const SELECTORS = {
  country: 'select[name$="[country]"]',
  zipcode: 'input[data-autocomplete="zipcode"]',
  houseNumber: 'input[data-autocomplete="address-number"]',
  houseNumberAddition: 'input[data-autocomplete="address-number-addition"]',
  street: 'input[data-autocomplete="street"]',
  city: 'input[data-autocomplete="city"]',
  extra: 'input[data-autocomplete="extra"]',
  failure: '[data-autocomplete="address-lookup-failure"]',
  autocompleteInput: 'input#autocomplete_address',
  manualInputToggle: '[data-autocomplete="manual-input-toggle"]',
  manualInputLink: '[data-autocomplete="manual-input-link"]',
}

let supportedCountries = []
let supportedCountriesMap = new Map()
let supportedCountriesPromise = null

/**
 * @param {HTMLElement} element
 * @returns {AddressLookupElements}
 */
const getAddressLookupElements = (element) => ({
  countryField: element.querySelector(SELECTORS.country),
  zipcodeField: element.querySelector(SELECTORS.zipcode),
  houseNumberField: element.querySelector(SELECTORS.houseNumber),
  houseNumberAdditionField: element.querySelector(SELECTORS.houseNumberAddition),
  streetField: element.querySelector(SELECTORS.street),
  cityField: element.querySelector(SELECTORS.city),
  extraField: element.querySelector(SELECTORS.extra),
  failureElement: element.querySelector(SELECTORS.failure),
  autocompleteInput: element.querySelector(SELECTORS.autocompleteInput),
  manualInputToggle: element.querySelector(SELECTORS.manualInputToggle),
  manualInputLink: element.querySelector(SELECTORS.manualInputLink),
})

/**
 * @param {HTMLInputElement} element
 * @returns {boolean}
 */
const isEmpty = (element) => element?.value.trim() === ''

/**
 * @param {string} value
 * @returns {string}
 */
const normalizeZipcode = (value) => String(value).replace(/\s/g, '')

/**
 * @param {AddressLookupElements} elements
 * @returns {boolean}
 */
const isDutchAddress = ({ countryField }) => countryField?.value === 'NL'

/**
 * @param {AddressLookupElements} elements
 * @returns {boolean}
 */
const isSupportedCountry = ({ countryField }) => {
  const country = countryField?.value

  return country && supportedCountries.some((c) => c.iso2 === country)
}

/**
 * @param {AddressLookupElements} elements
 * @returns {boolean}
 */
const canLookupAddress = (elements) => {
  if (!isDutchAddress(elements)) {
    return false
  }

  const zipcode = normalizeZipcode(elements.zipcodeField?.value ?? '')
  const houseNumber = Number.parseInt(String(elements.houseNumberField?.value ?? ''), 10)

  return zipcode.length === 6 && houseNumber > 0
}

/**
 * @returns {string}
 */
const generateSessionId = () => {
  const length = 32
  const characterSet = '0123456789abcdef'
  const randomIntegers = new Uint8Array(length)

  window.crypto.getRandomValues(randomIntegers)

  return Array.from(randomIntegers, (byte) => characterSet[byte % characterSet.length]).join('')
}

/**
 * @param {AddressLookupElements} elements
 * @returns {boolean}
 */
const shouldRevealLookupFailure = (elements) => {
  return isDutchAddress(elements) && canLookupAddress(elements) && isEmpty(elements.streetField) && isEmpty(elements.cityField)
}

/**
 * @param {HTMLElement} failureElement
 * @param {boolean} isVisible
 */
const setLookupFailureVisibility = (failureElement, isVisible) => {
  if (!failureElement) {
    return
  }

  failureElement.classList.toggle('d-none', !isVisible)
}

/**
 * @param {HTMLElement} element
 * @param {string} api
 * @returns {Promise<NLPostcodeResponse|undefined>}
 */
export const lookupAddress = async (element, api) => {
  const elements = getAddressLookupElements(element)

  if (!canLookupAddress(elements)) {
    return
  }

  const url = new URL(api)
  const zipcode = normalizeZipcode(elements.zipcodeField.value)
  const houseNumber = Number.parseInt(elements.houseNumberField.value, 10)
  const query = new URLSearchParams({ zipcode, 'address-number': houseNumber }).toString()

  elements.streetField.readOnly = true
  elements.cityField.readOnly = true

  try {
    const response = await fetch(`${url}?${query}`)

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error')
      const error = new Error(`NL postcode lookup failed: ${response.status} ${response.statusText}`)
      error.cause = { status: response.status, statusText: response.statusText, body: errorText }

      throw error
    }

    return await response.json()
  } finally {
    elements.streetField.readOnly = false
    elements.cityField.readOnly = false
  }
}

const lookupAddressDebounced = debouncePromise(lookupAddress, 250)

/**
 * @param {string} api
 * @returns {Promise<Array>}
 */
const fetchSupportedCountries = async (api) => {
  if (supportedCountriesPromise) {
    return supportedCountriesPromise
  }

  supportedCountriesPromise = (async () => {
    try {
      const response = await fetch(api)
      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error')
        logger.error('Failed to fetch supported countries:', {
          status: response.status,
          statusText: response.statusText,
          body: errorText,
        })
        return []
      }
      const data = await response.json()

      // Build a map from iso2 to iso3 for easy lookup
      if (data && Array.isArray(data)) {
        data.forEach((country) => {
          if (country.iso2 && country.iso3) {
            supportedCountriesMap.set(country.iso2, country.iso3.toLowerCase())
          }
        })
      }

      return data || []
    } catch (e) {
      logger.error('Exception fetching supported countries:', e)

      return []
    }
  })()

  return supportedCountriesPromise
}

/**
 * @param {string} iso2
 * @returns {string}
 */
const getCountryIso3 = (iso2) => supportedCountriesMap.get(iso2) || iso2.toLowerCase()

/**
 * @param {string} api
 * @param {string} context
 * @param {string} term
 * @param {string} sessionId
 * @param {AbortSignal} signal
 * @param {string} language
 * @returns {Promise<AutocompleteResponse>}
 */
const autocompleteAddress = async (api, context, term, sessionId, signal, language = 'en') => {
  const url = new URL(api, window.location.origin)
  const params = new URLSearchParams({
    context,
    term,
    language,
    buildingListMode: 'paged',
  })

  try {
    const response = await fetch(`${url}?${params}`, {
      headers: {
        'X-Autocomplete-Session': sessionId,
      },
      signal,
    })

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error')
      const error = new Error(`Autocomplete address failed: ${response.status} ${response.statusText}`)
      error.cause = { status: response.status, statusText: response.statusText, body: errorText }

      throw error
    }

    return await response.json()
  } catch (e) {
    if (e.name === 'AbortError') {
      logger.log('Autocomplete request aborted')
    } else {
      logger.error('Autocomplete address error:', e)
    }

    return { matches: [] }
  }
}

/**
 * @param {string} api
 * @param {string} context
 * @param {string} sessionId
 * @returns {Promise<AddressDetailsResponse|null>}
 */
const getAddressDetails = async (api, context, sessionId) => {
  const url = new URL(api, window.location.origin)

  // Build query string manually to ensure spaces are encoded as %20 (not +)
  const params = `context=${encodeURIComponent(context)}`

  try {
    const response = await fetch(`${url}?${params}`, {
      headers: {
        'X-Autocomplete-Session': sessionId,
      },
    })

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error')
      const error = new Error(`Get address details failed: ${response.status} ${response.statusText}`)
      error.cause = { status: response.status, statusText: response.statusText, body: errorText }

      throw error
    }

    return await response.json()
  } catch (error) {
    logger.error('Get address details error:', error)

    return null
  }
}

/**
 * @param {string} country
 * @param {AddressLookupElements|HTMLElement[]} elements
 */
const enableFields = (country, elements) => {
  const values = Array.isArray(elements) ? elements : Object.values(elements)

  for (const element of values) {
    element.readOnly = false
    element.classList.remove('disabled')
  }
}

/**
 * @param {string} country
 * @param {AddressLookupElements|HTMLElement[]} elements
 */
const disableFields = (country, elements) => {
  const values = Array.isArray(elements) ? elements : Object.values(elements)

  for (const element of values) {
    element.readOnly = true
    element.classList.add('disabled')
  }
}

/**
 * @param {AddressLookupElements} elements
 * @param {NLPostcodeResponse} data
 */
const handleSuccessfulResponse = (elements, data) => {
  setLookupFailureVisibility(elements.failureElement, false)

  elements.streetField.value = data.street
  elements.cityField.value = data.city

  const options = data.houseNumberAdditions.filter(Boolean).map((item) => ({ value: item, text: item }))
  const ts = elements.houseNumberAdditionField.tomselect

  if (!options.length) {
    if (ts) {
      ts.clear()
      ts.destroy()
    }

    return
  }

  if (ts) {
    ts.clear()
    ts.clearOptions()
    ts.addOptions(options)
    ts.focus()

    return
  }

  new TomSelect(elements.houseNumberAdditionField, {
    maxItems: 1,
    options,
    plugins: ['clear_button'],
  })
}

/**
 * @param {HTMLElement} element
 */
const showAutocomplete = (element) => {
  element?.closest('.col-12')?.classList.remove('d-none')
}

/**
 * @param {HTMLElement} element
 */
const hideAutocomplete = (element) => {
  element?.closest('.col-12')?.classList.add('d-none')
}

/**
 * @param {HTMLElement} element
 */
const showManualInput = (element) => {
  element?.classList.remove('d-none')
}

/**
 * @param {HTMLElement} element
 */
const hideManualInput = (element) => {
  element?.classList.add('d-none')
}

/**
 * @param {AddressLookupElements} elements
 */
const handleCountryChange = (elements) => {
  const country = elements.countryField?.value

  if (!country) {
    return
  }

  logger.debug('[Address Lookup] Country changed to:', country)

  // disable all fields by default, then selectively enable based on country
  disableFields(country, [elements.zipcodeField, elements.houseNumberField, elements.houseNumberAdditionField, elements.streetField, elements.cityField])

  if (country === 'NL') {
    // For NL, enable zipcode, house number, and house number addition fields
    enableFields(country, [elements.zipcodeField, elements.houseNumberField, elements.houseNumberAdditionField])
    hideAutocomplete(elements.autocompleteInput)
    showManualInput(elements.manualInputToggle)

    return
  }

  if (isSupportedCountry(elements)) {
    // For supported countries, enable the autocomplete input and manual input toggle
    showAutocomplete(elements.autocompleteInput)
    showManualInput(elements.manualInputToggle)

    return
  }

  // For unsupported countries, enable all fields for manual input
  enableFields(country, [elements.zipcodeField, elements.houseNumberField, elements.houseNumberAdditionField, elements.streetField, elements.cityField])
  hideAutocomplete(elements.autocompleteInput)
  hideManualInput(elements.manualInputToggle)
}

/**
 * @param {AddressLookupElements} elements
 */
const clearAddressFields = (elements) => {
  const fields = [
    { field: elements.streetField, value: '' },
    { field: elements.houseNumberField, value: '' },
    { field: elements.zipcodeField, value: '' },
    { field: elements.cityField, value: '' },
  ]

  fields.forEach(({ field, value }) => {
    field.value = value
  })

  const ts = elements.houseNumberAdditionField.tomselect

  if (ts) {
    ts.clear()
    ts.clearOptions()
    ts.destroy()
  }

  elements.houseNumberAdditionField.value = ''
}

/**
 * @param {AddressLookupElements} elements
 * @param {AddressDetailsAddress} address
 */
const populateAddressFields = (elements, address) => {
  const fields = [
    { field: elements.streetField, value: address.street },
    { field: elements.houseNumberField, value: address.buildingNumber },
    { field: elements.zipcodeField, value: address.postcode },
    { field: elements.cityField, value: address.locality },
  ]

  fields.forEach(({ field, value }) => {
    field.value = value || ''
  })

  const ts = elements.houseNumberAdditionField.tomselect

  if (ts) {
    ts.clear()
    ts.clearOptions()
    ts.destroy()
  }

  elements.houseNumberAdditionField.value = address.buildingNumberAddition || ''
}

/**
 * Initialize country change handling
 * @param {AddressLookupElements} elements
 */
const initializeCountryChangeHandler = (elements) => {
  if (!elements.countryField) {
    return
  }

  elements.countryField.addEventListener('change', () => handleCountryChange(elements))

  handleCountryChange(elements)
}

/**
 * Initialize manual input toggle
 * @param {AddressLookupElements} elements
 */
const initializeManualInputToggle = (elements) => {
  if (!elements.manualInputLink) {
    return
  }

  elements.manualInputLink.addEventListener('click', (event) => {
    event.preventDefault()

    enableFields(elements.countryField.value, [
      elements.zipcodeField,
      elements.houseNumberField,
      elements.houseNumberAdditionField,
      elements.streetField,
      elements.cityField,
    ])
    hideAutocomplete(elements.autocompleteInput)
    hideManualInput(elements.manualInputToggle)
  })
}

/**
 * Initialize NL postcode lookup on keyup
 * @param {HTMLElement} element
 * @param {AddressLookupElements} elements
 * @param {string} postcodeApi
 */
const initializeNLPostcodeLookup = (element, elements, postcodeApi) => {
  element.addEventListener('keyup', (event) => {
    const target = event.target

    if (!(target instanceof HTMLInputElement)) {
      return
    }

    if (!target.matches(`${SELECTORS.zipcode}, ${SELECTORS.houseNumber}`)) {
      return
    }

    if (!canLookupAddress(elements)) {
      return
    }

    lookupAddressDebounced(element, postcodeApi).then(
      (data) => {
        if (data) {
          handleSuccessfulResponse(elements, data)
        }
      },
      () => {
        /* ignore errors or canceled dangling promises */
      },
    )
  })
}

/**
 * Initialize NL postcode lookup failure notification
 * @param {HTMLElement} element
 * @param {AddressLookupElements} elements
 * @param {string} postcodeApi
 */
const initializeNLPostcodeFailureNotification = (element, elements, postcodeApi) => {
  element.addEventListener('focusin', (event) => {
    const target = event.target

    if (!(target instanceof HTMLInputElement) || !target.matches(SELECTORS.street)) {
      return
    }

    if (!shouldRevealLookupFailure(elements)) {
      return
    }

    lookupAddress(element, postcodeApi).then(
      (data) => {
        handleSuccessfulResponse(elements, data)
      },
      () => {
        setLookupFailureVisibility(elements.failureElement, true)
      },
    )
  })
}

/**
 * Create TomSelect configuration for autocomplete
 * @param {AddressLookupElements} elements
 * @param {string} autocompleteApi
 * @param {string} sessionId
 * @param {Object} state
 * @param {Function} matchCallback
 * @returns {Object}
 */
const createTomSelectConfig = (elements, autocompleteApi, sessionId, state, matchCallback) => ({
  valueField: 'value',
  searchField: [],
  placeholder: elements.autocompleteInput.placeholder || 'Begin met typen...',
  loadThrottle: 300,
  preload: false,
  openOnFocus: false,
  maxItems: 1,
  plugins: ['clear_button'],
  highlight: false,
  shouldLoad: function (query) {
    return query.length > 1
  },
  load: async function (query, callback) {
    if (state.abortController) {
      state.abortController.abort()
    }

    state.abortController = new AbortController()

    if (!query.length || !isSupportedCountry(elements)) {
      callback()
      return
    }

    const country = elements.countryField?.value
    const context = state.currentContext || getCountryIso3(country)

    logger.log('Autocomplete load called:', { query, context, currentContext: state.currentContext })

    try {
      const data = await autocompleteAddress(autocompleteApi, context, query, sessionId, state.abortController.signal)

      logger.log('Autocomplete response:', data)

      if (data.newContext && typeof data.newContext === 'string') {
        logger.log('Context switched from', state.currentContext, 'to', data.newContext)

        state.currentContext = data.newContext
      }

      const matches = data.matches || []

      logger.log('Matches found:', matches.length, matches)

      this.clearOptions()

      callback(matches)

      logger.log('Callback called, TomSelect should now have', Object.keys(this.options).length, 'options')
    } catch (e) {
      if (e.name !== 'AbortError') {
        logger.error('Autocomplete error:', e)
      }

      callback()
    }
  },
  onChange: async function (value) {
    if (!value) {
      return
    }

    const selectedOption = this.options[value]

    if (!selectedOption) {
      return
    }

    logger.log('Selected option:', selectedOption)

    if (selectedOption.precision !== 'Address') {
      state.currentContext = selectedOption.context
    }

    await matchCallback(selectedOption)

    if (selectedOption.precision !== 'Address') {
      logger.log('Drilling down with value:', selectedOption.value, 'context:', state.currentContext)

      this.clear()
      this.clearOptions()
      this.setTextboxValue(selectedOption.label)
      this.load(selectedOption.value)

      return
    }

    logger.log('Final address selected:', selectedOption)

    this.clearOptions()
    this.close()
    this.blur()
  },
  render: {
    option: function (data, escape) {
      return `<div>
        <span class="label">${escape(data.label)}</span>
        ${data.description ? `<span class="description" style="color: #999; font-size: 0.9em; margin-left: 0.5em;">${escape(data.description)}</span>` : ''}
      </div>`
    },
    item: function (data, escape) {
      return `<div>${escape(data.label || data.value)}</div>`
    },
  },
})

/**
 * Initialize international autocomplete
 * @param {AddressLookupElements} elements
 * @param {string} autocompleteApi
 * @param {string} detailsApi
 * @param {string} sessionId
 */
const initializeInternationalAutocomplete = (elements, autocompleteApi, detailsApi, sessionId) => {
  if (!elements.autocompleteInput) {
    return
  }

  const state = {
    currentContext: null,
    abortController: null,
  }

  /**
   * @param {AutocompleteMatch} match
   * @returns {Promise<void>}
   */
  const fillAddressFromMatch = async (match) => {
    if (match.precision !== 'Address') {
      return
    }

    const details = await getAddressDetails(detailsApi, match.context, sessionId)

    if (details?.address) {
      populateAddressFields(elements, details.address)
    } else {
      clearAddressFields(elements)
    }

    state.currentContext = null
  }

  const config = createTomSelectConfig(elements, autocompleteApi, sessionId, state, fillAddressFromMatch)
  const tomSelectInstance = new TomSelect(elements.autocompleteInput, config)

  elements.autocompleteInput.tomselect = tomSelectInstance
}

/**
 * @param {HTMLElement} container
 * @param {string} postcodeApi
 * @param {string} autocompleteApi
 * @param {string} detailsApi
 * @param {string} supportedCountriesApi
 */
export default async (container, postcodeApi, autocompleteApi, detailsApi, supportedCountriesApi) => {
  logger.debug('[Address Lookup] Initializing address lookup for container:', container)

  const elements = getAddressLookupElements(container)
  const sessionId = generateSessionId()

  logger.debug('[Address Lookup] Generated autocomplete session ID:', sessionId)

  if (supportedCountriesApi) {
    logger.debug('[Address Lookup] Fetching supported countries from API:', supportedCountriesApi)

    supportedCountries = await fetchSupportedCountries(supportedCountriesApi)
  }

  initializeCountryChangeHandler(elements)
  initializeManualInputToggle(elements)
  initializeNLPostcodeLookup(container, elements, postcodeApi)
  initializeNLPostcodeFailureNotification(container, elements, postcodeApi)
  initializeInternationalAutocomplete(elements, autocompleteApi, detailsApi, sessionId)
}
